import React, { useEffect, useRef, useState } from "react";
import { Send, Sparkles } from "lucide-react";
import { useLocation } from "react-router-dom";
import TextButton from "../primitives/TextButton";
import { assistantChat, ApiError } from "../../services/api";

const GREETING = {
    role: "assistant",
    content:
        "Hi — I can answer questions about packing, SKUs, and your plan. If I can't help, I'll pass it to the team.",
};

/**
 * Chat tab of the feedback widget. The transcript lives here in component
 * state and is resent with each turn — the API is stateless, so there's no
 * session to restore and nothing to clean up when the panel closes.
 */
export default function AssistantChat() {
    const [turns, setTurns] = useState([GREETING]);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [errMsg, setErrMsg] = useState(null);
    const location = useLocation();
    const scrollRef = useRef(null);

    useEffect(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [turns, sending]);

    const send = async () => {
        const trimmed = draft.trim();
        if (!trimmed || sending) return;

        // Drop the canned greeting before sending — it isn't a real turn.
        const history = turns.filter((t) => t !== GREETING);
        setTurns((prev) => [...prev, { role: "user", content: trimmed }]);
        setDraft("");
        setErrMsg(null);
        setSending(true);
        try {
            const res = await assistantChat({
                message: trimmed,
                history,
                page: location.pathname,
            });
            setTurns((prev) => [
                ...prev,
                { role: "assistant", content: res.reply, escalated: res.escalated },
            ]);
        } catch (err) {
            setErrMsg(
                err instanceof ApiError
                    ? err.message
                    : "The assistant is unavailable right now."
            );
        } finally {
            setSending(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    return (
        <div className="fbw__chat">
            <div className="fbw__transcript" ref={scrollRef} aria-live="polite">
                {turns.map((turn, i) => (
                    <div
                        key={i}
                        className={`fbw__turn fbw__turn--${turn.role}`}
                    >
                        {turn.role === "assistant" && (
                            <Sparkles
                                size={12}
                                className="fbw__turn-icon"
                                aria-hidden="true"
                            />
                        )}
                        <span>{turn.content}</span>
                        {turn.escalated && (
                            <span className="fbw__escalated">
                                Sent to the team
                            </span>
                        )}
                    </div>
                ))}
                {sending && (
                    <div className="fbw__turn fbw__turn--assistant fbw__turn--typing">
                        <span className="fbw__dot" />
                        <span className="fbw__dot" />
                        <span className="fbw__dot" />
                    </div>
                )}
            </div>

            {errMsg && (
                <p className="fbw__chat-error" role="alert">
                    {errMsg}
                </p>
            )}

            <div className="fbw__composer">
                <textarea
                    className="fbw__input"
                    rows={2}
                    maxLength={4000}
                    value={draft}
                    placeholder="Ask a question…"
                    aria-label="Message the assistant"
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKeyDown}
                />
                <TextButton
                    variant="primary"
                    icon={Send}
                    loading={sending}
                    disabled={!draft.trim()}
                    onClick={send}
                >
                    Send
                </TextButton>
            </div>
        </div>
    );
}
